// Maps every way an outbound call can end short of a conversation to the copy
// the call card shows, split into two classes (SYN-367):
//
//   outcome — the call did what calls do (nobody answered, the line was busy,
//             the number is switched off). Neutral styling, no reference ID,
//             no support path.
//   failure — something went wrong placing the call (bad number, blocked
//             mic, lost session). Error styling, with the Twilio Call SID
//             available as a copyable "Reference ID" under a collapsed
//             Details section, never inline in the sentence.
//
// Every mapper returns { kind, message, code }. The SDK's own messages
// ("UnknownError (31000)") and the Call SID are meaningless to the people
// placing calls; the raw code and the full SDK error still go to the client
// logs verbatim via describeTwilioError.
//
// Codes: https://www.twilio.com/docs/api/errors — the subsets the browser SDK
// actually emits are the 20xxx token errors, the 31xxx client/signaling
// errors, and the 53xxx connection errors.

export const OUTCOME = 'outcome';
export const FAILURE = 'failure';

// --- Outcomes ---------------------------------------------------------------

const NO_ANSWER_COPY = 'No answer.';
const BUSY_COPY = 'The line was busy.';
const DECLINED_COPY = 'The call was declined.';
const UNAVAILABLE_COPY = 'The number is unavailable right now.';
// Remote disconnect before the dialed leg accepted. No server poll follows a
// bare disconnect, so this label is final.
export const ENDED_BEFORE_CONNECT_COPY = 'Call ended before it connected.';

// --- Failures ---------------------------------------------------------------

const BAD_NUMBER_COPY = 'This number could not be reached. Check that the number is correct.';
const NOT_PERMITTED_COPY = 'Calls to this number are not permitted.';
const CONTACT_SUPPORT_COPY = 'This call could not be placed. Contact support if it continues.';
const REJECTED_BY_US_COPY =
    'This call could not be placed. The patient may have no dialable phone number on file, or calling may be disabled for this clinic.';
const BROWSER_MIC_COPY = 'Could not start the call from this browser. Check microphone permissions and try again.';
const MIC_UNAVAILABLE_COPY = 'Could not access your microphone. Check that it is connected and not in use by another app.';
const BROWSER_SESSION_COPY = 'Could not start the call from this browser. Refresh the page and try again.';
const BROWSER_INTERNET_COPY = 'Could not start the call from this browser. Check your internet connection and try again.';
const BROWSER_NETWORK_COPY = 'Could not start the call from this browser. Check your network connection and try again.';
const DISCONNECTED_COPY = 'The call was disconnected unexpectedly. Try again.';
const GENERIC_COPY = 'Something went wrong placing this call.';

// Exported for the controller's two failure paths that carry no Twilio error:
// the mic preflight timeout (no getUserMedia rejection to map) and the
// access-token fetch failing before the SDK is involved.
export const MIC_TIMEOUT_COPY = BROWSER_MIC_COPY;
export const SERVER_UNREACHABLE_COPY = BROWSER_INTERNET_COPY;

// SDK codes that are ordinary outcomes on the dialed leg, not failures.
const TWILIO_OUTCOME_COPY = {
    31480: UNAVAILABLE_COPY, // Temporarily Unavailable (SIP 480): switched off / out of service
    31486: BUSY_COPY, // Busy Here (SIP 486)
    31603: DECLINED_COPY // Decline (SIP 603)
};

const TWILIO_ERROR_COPY = {
    // Access-token problems (fetched fresh per call, so these usually mean a
    // clock/session issue rather than staleness).
    20101: BROWSER_SESSION_COPY, // AccessTokenInvalid
    20104: BROWSER_SESSION_COPY, // AccessTokenExpired
    31204: BROWSER_SESSION_COPY, // Invalid JWT token
    31205: BROWSER_SESSION_COPY, // JWT token expired

    // Microphone. 31208 is the twilio.js 1.x code, 31401/31402 the voice-sdk
    // 2.x equivalents — keep both in case the SDK maps either through.
    31208: BROWSER_MIC_COPY, // PermissionDeniedError (legacy)
    31401: BROWSER_MIC_COPY, // UserMediaErrors.PermissionDeniedError
    31402: MIC_UNAVAILABLE_COPY, // UserMediaErrors.AcquisitionFailedError

    // Signaling / transport between the browser and Twilio.
    // 31005 deliberately does NOT get the check-your-internet copy: in
    // production it is almost always "Error sent from gateway in HANGUP" —
    // Twilio terminating the call from its side (dial-leg failure), not the
    // clinician's network. These carry a CallSid, so the post-error server
    // poll upgrades the card to the real reason (or to an outcome) when the
    // status callbacks say so.
    31005: DISCONNECTED_COPY,
    31009: BROWSER_INTERNET_COPY, // SignalingErrors.TransportError
    53000: BROWSER_INTERNET_COPY, // Signaling connection error
    53001: BROWSER_INTERNET_COPY, // Signaling connection disconnected
    53405: BROWSER_NETWORK_COPY, // Media connection failed (ICE)

    // Call setup. 31000 is Twilio's catch-all for server-side dial rejections
    // (unroutable number, geo permissions) — the SYN-366 "UnknownError
    // (31000)". 31002 is what our own TwiML <Reject/> surfaces as (patient has
    // no phone number on file, calling disabled for clinic).
    31000: GENERIC_COPY,
    31002: REJECTED_BY_US_COPY,
    31003: GENERIC_COPY // timed out before connect
};

// Server-persisted dial failures (Twilio 132xx TwiML errors). These never
// reach the browser SDK — Twilio's status callback writes them onto the
// OutboundCall row and the controller fetches them after a terminal SDK error
// to replace the generic copy.
const SERVER_DIAL_ERROR_COPY = {
    13223: BAD_NUMBER_COPY, // not in a dialable format
    13224: BAD_NUMBER_COPY, // invalid or out of service
    13225: NOT_PERMITTED_COPY, // premium or restricted number
    13227: CONTACT_SUPPORT_COPY // region not enabled for our account
};

// A FAILED row with no ErrorCode means Twilio's callback carried none — the
// carrier rejected the dial without a specific reason.
const CARRIER_REJECTED_COPY = BAD_NUMBER_COPY;

// Terminal outcomes on the dialed leg that aren't failures at all. Twilio's
// gateway can attach a generic 31000 to the HANGUP of an ordinary unanswered
// dial, which lands the call in the failure card — but the status callbacks
// have already recorded what really happened. When the server says the
// dialed leg simply ended NO_ANSWER or BUSY, the outcome is the honest copy.
const SERVER_OUTCOME_COPY = {
    NO_ANSWER: NO_ANSWER_COPY,
    BUSY: BUSY_COPY
};

function outcome(message) {
    return { kind: OUTCOME, message, code: null };
}

function failure(message, code) {
    return { kind: FAILURE, message, code };
}

/**
 * Map a server-confirmed non-failure outcome on the dialed (child) leg to
 * card copy, or null when the outcome isn't one we can say anything more
 * honest about (COMPLETED / CANCELED — keep the SDK's copy).
 *
 * @param {string | null | undefined} childStatus
 * @returns {{ kind: 'outcome', message: string, code: null } | null}
 */
export function friendlyServerOutcome(childStatus) {
    const message = SERVER_OUTCOME_COPY[childStatus];
    if (message) {
        return outcome(message);
    }
    return null;
}

/**
 * Map the server's persisted view of a FAILED OutboundCall row to card copy.
 * Always returns a payload — the caller has already established the call
 * failed; this only decides how specifically we can say why.
 *
 * @param {{ errorCode: string | number | null | undefined }} row
 * @returns {{ kind: 'failure', message: string, code: number | null }}
 */
export function friendlyServerCallError({ errorCode }) {
    const code = Number.parseInt(errorCode, 10);
    if (Number.isFinite(code) && SERVER_DIAL_ERROR_COPY[code]) {
        return failure(SERVER_DIAL_ERROR_COPY[code], code);
    }
    if (Number.isFinite(code)) {
        return failure(GENERIC_COPY, code);
    }
    return failure(CARRIER_REJECTED_COPY, null);
}

/**
 * Map a getUserMedia rejection (from the controller's mic preflight) to the
 * callError payload. NotAllowedError/PermissionDeniedError → 31401 and the
 * fall-through (no mic, mic in use, hardware failure) → 31402 mirror the
 * Twilio SDK's own classification in call.accept(). SecurityError is a
 * deliberate divergence: the SDK would lump it into 31402
 * (AcquisitionFailedError), but it means mic access is blocked by policy
 * (insecure context, permissions policy) — not a flaky device — so the
 * blocked copy is the accurate one.
 *
 * @param {unknown} err DOMException (or plain error) from getUserMedia
 * @returns {{ kind: 'failure', message: string, code: number }}
 */
export function friendlyUserMediaError(err) {
    if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError' || err?.name === 'SecurityError') {
        return failure(BROWSER_MIC_COPY, 31401);
    }
    return failure(MIC_UNAVAILABLE_COPY, 31402);
}

/**
 * Convert a Twilio SDK (or plain) error into the payload the slice stores:
 * an outcome for the SDK codes that are ordinary call results, mapped
 * failure copy when the code is known, generic copy + the raw code when it
 * isn't. An error with no code at all (non-Twilio failure, e.g. a thrown
 * setup error) gets the generic copy too — its own message is for the logs.
 *
 * @param {unknown} err
 * @returns {{ kind: 'outcome' | 'failure', message: string, code: number | null }}
 */
export function friendlyCallError(err) {
    const code = typeof err?.code === 'number' ? err.code : null;
    if (code !== null && TWILIO_OUTCOME_COPY[code]) {
        return outcome(TWILIO_OUTCOME_COPY[code]);
    }
    if (code !== null && TWILIO_ERROR_COPY[code]) {
        return failure(TWILIO_ERROR_COPY[code], code);
    }
    return failure(GENERIC_COPY, code);
}

/**
 * Decompose a Twilio SDK error into a plain loggable object. TwilioError
 * carries diagnostic fields (causes, description, explanation, originalError)
 * beyond message/code — losing them was why 31000 tickets were undebuggable
 * from the logs. undefined fields drop out of the JSON batch payload.
 *
 * @param {unknown} err
 */
export function describeTwilioError(err) {
    return {
        error: err?.message,
        code: err?.code ?? null,
        name: err?.name ?? undefined,
        description: err?.description ?? undefined,
        explanation: err?.explanation ?? undefined,
        causes: Array.isArray(err?.causes) && err.causes.length > 0 ? err.causes : undefined,
        originalError: err?.originalError?.message ?? undefined
    };
}
